import { useTranslation } from "react-i18next";
import styles from "./ViewRoutingTable.module.scss";
import Icon from "../../../common/Icon/Icon";
import { useAppSelector } from "../../../../redux/hooks";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Approver,
  useViewRoutingContext,
} from "../ViewRoutingContext/ViewRoutingContext";
import { useMemo, useRef } from "react";
import { countryCodeMappings } from "../../../../constants/dataConstants";
import { formatDate } from "../../../../utils/dateFormatter";

const ViewRoutingTable: React.FC = () => {
  const { t } = useTranslation();

  const {
    approvers: items,
    setApprovers: setItems,
    viewOnly,
  } = useViewRoutingContext();

  const tableColumns = useMemo(
    () =>
      [
        "assigned_approvers",
        "status",
        "date",
        !viewOnly && "actions",
      ].filter<string>(Boolean as any),
    [viewOnly]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <div
      className={`table_container view_routing_table_wrapper ${styles.table_wrapper}`}
    >
      <table className="view_routing_table">
        <thead className={styles.tbl_header_wrapper}>
          <tr className={`tbl_header ${styles.tbl_header}`}>
            {tableColumns.map((column) => (
              <th key={column} className={`header_cell ${styles.header_cell}`}>
                <span>{t(`hatv_view_routing.table_columns.${column}`)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <tbody className={styles.table_body}>
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {items.length ? (
                items.map((data) => <TableRowItem key={data.id} data={data} />)
              ) : (
                <tr className={`tbl_data_row ${styles.tbl_data_row}`}>
                  <td
                    className={`data_cell emptyDataCell ${styles.data_cell} ${styles.emptyDataCell}`}
                    colSpan={4}
                  >
                    {t(`hatv_view_routing.no_approver_assigned`)}
                  </td>
                </tr>
              )}
            </SortableContext>
          </tbody>
        </DndContext>
      </table>
    </div>
  );
};

const DELETE_MESSAGE_TIMEOUT = 6000;

const TableRowItem: React.FC<{
  data: Approver;
}> = ({ data }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: data.id });

  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

      const userDetails = useAppSelector((state: any) => state.userDetails.user);
      const userAffiliateCode = userDetails?.userAffiliateCode || 0;

  const { setApprovers: setItems, viewOnly } = useViewRoutingContext();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
          boxShadow: "0px 2px 15px 0px #000000CC",
          zIndex: 10,
        }
      : {}),
  };

  const deleteTimeoutId = useRef<ReturnType<typeof setTimeout>>();

  const deleteApprover = () => {
    setItems((items) => items.filter((item) => item.id !== data.id));
  };
  const handleDelete = () => {
    setItems((items) =>
      items.map((item) =>
        item.id === data.id
          ? {
              ...item,
              deleted: true,
            }
          : item
      )
    );

    deleteTimeoutId.current = setTimeout(
      deleteApprover,
      DELETE_MESSAGE_TIMEOUT
    );
  };

  const handleUndoDelete = () => {
    setItems((items) =>
      items.map((item) =>
        item.id === data.id
          ? {
              ...item,
              deleted: false,
            }
          : item
      )
    );
    if (deleteTimeoutId.current) clearTimeout(deleteTimeoutId.current);
  };

  const handleDismissDelete = () => {
    deleteApprover();
    if (deleteTimeoutId.current) clearTimeout(deleteTimeoutId.current);
  };

  if (data.deleted) {
    return (
      <tr className={`tbl_data_row ${styles.tbl_data_row}`}>
        <td
          className={`data_cell ${styles.data_cell} ${styles.deleted_cell}`}
          colSpan={4}
        >
          <span>
            {data.name} {t(`hatv_view_routing.removed_from_list`)}
          </span>
          <div className={styles.deleted_actions}>
            <button onClick={handleUndoDelete}>
              {t(`hatv_view_routing.undo`)}
            </button>
            <button onClick={handleDismissDelete}>
              {t(`hatv_view_routing.dismiss`)}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  const approvalStatus = t(`hatv_view_routing.approval_status.${data.status}`);

  return (
    <tr
      className={`tbl_data_row ${styles.tbl_data_row}`}
      ref={setNodeRef}
      style={style}
    >
      <td className={`data_cell ${styles.data_cell}`}>
        {!viewOnly && (
          <div
            className={`drag_handle ${styles.drag_handle} `}
            {...attributes}
            {...listeners}
          >
            <Icon
              size={16}
              name="drag_handle_icon"
              color={currentTheme === "light" ? "#2c2c2c" : "#AEAEB2"}
            />
            <Icon
              size={16}
              name="drag_handle_icon"
              color={currentTheme === "light" ? "#2c2c2c" : "#AEAEB2"}
            />
          </div>
        )}
        <div className={`${!viewOnly ? styles.with_drag_handle : ""}`}>
          <p>{data.name}</p>
          {/* <p>{data.designation}</p> */}
        </div>
      </td>
      <td className={`data_cell ${styles.data_cell}`}>
        <div>
          <p>{approvalStatus || "-"}</p>
        </div>
      </td>
      <td className={`data_cell ${styles.data_cell}`}>
        <div>
          <p>
            {data.date ? formatDate(
              data.date,
              countryCodeMappings[userAffiliateCode],
            ): "-"}
          </p>
        </div>
      </td>
      {!viewOnly && (
        <td className={`data_cell ${styles.data_cell} ${styles.actions}`}>
          <button onClick={handleDelete}>
            <Icon
              size={16}
              name="trash"
              color={currentTheme === "light" ? "#2c2c2c" : "#D8D8D9"}
            />
          </button>
        </td>
      )}
    </tr>
  );
};

export default ViewRoutingTable;
